export interface PendulumState {
  theta: number;
  omega: number;
  time: number;
}

export interface PendulumConfig {
  g?: number;
  L?: number;
  theta0?: number;
  omega0?: number;
  dampingCoefficient?: number;
  dt?: number;
  duration?: number;
  sampleRate?: number;
  mass?: number;
}

export const DEFAULT_PENDULUM_CONFIG: Required<Omit<PendulumConfig, 'omega0' | 'theta0'>> & { theta0: number; omega0: number } = {
  g: 9.81,
  L: 1,
  theta0: 0.2,
  omega0: 0,
  dampingCoefficient: 0.02,
  dt: 0.001,
  duration: 5,
  sampleRate: 60,
  mass: 1,
};

export function derivative(state: PendulumState, config: PendulumConfig): { theta: number; omega: number } {
  const g = config.g ?? DEFAULT_PENDULUM_CONFIG.g;
  const L = config.L ?? DEFAULT_PENDULUM_CONFIG.L;
  const dampingCoefficient = config.dampingCoefficient ?? DEFAULT_PENDULUM_CONFIG.dampingCoefficient;
  const theta = Number(state.theta);
  const omega = Number(state.omega);

  if (!Number.isFinite(theta) || !Number.isFinite(omega)) {
    throw new RangeError('Pendulum state must contain finite theta and omega values');
  }

  return {
    theta: omega,
    omega: -(g / L) * Math.sin(theta) - dampingCoefficient * omega,
  };
}

export function rk4Step(state: PendulumState, dt: number, config: PendulumConfig): PendulumState {
  const current = { theta: Number(state.theta), omega: Number(state.omega), time: Number(state.time) };

  const k1 = derivative(current, config);
  const k2 = derivative({
    theta: current.theta + 0.5 * k1.theta * dt,
    omega: current.omega + 0.5 * k1.omega * dt,
    time: current.time,
  }, config);
  const k3 = derivative({
    theta: current.theta + 0.5 * k2.theta * dt,
    omega: current.omega + 0.5 * k2.omega * dt,
    time: current.time,
  }, config);
  const k4 = derivative({
    theta: current.theta + k3.theta * dt,
    omega: current.omega + k3.omega * dt,
    time: current.time,
  }, config);

  const nextTheta = current.theta + (dt / 6) * (k1.theta + 2 * k2.theta + 2 * k3.theta + k4.theta);
  const nextOmega = current.omega + (dt / 6) * (k1.omega + 2 * k2.omega + 2 * k3.omega + k4.omega);

  return {
    theta: nextTheta,
    omega: nextOmega,
    time: current.time + dt,
  };
}

export function pendulumEnergy(theta: number, omega: number, L: number, g: number, mass = 1): number {
  const gravitationalPotential = mass * g * L * (1 - Math.cos(theta));
  const kinetic = 0.5 * mass * (L * omega) ** 2;
  return gravitationalPotential + kinetic;
}

export function smallAnglePeriod(L: number, g = 9.81): number {
  return 2 * Math.PI * Math.sqrt(L / g);
}

export function largeAnglePeriodCorrection(thetaRadians: number): number {
  return 1 + (thetaRadians ** 2) / 16 + (11 * thetaRadians ** 4) / 3072;
}

export function apparentPeriod(L: number, g = 9.81, thetaRadians = 0.1): number {
  return smallAnglePeriod(L, g) * largeAnglePeriodCorrection(thetaRadians);
}

export function solvePendulum(config: PendulumConfig) {
  const merged: Required<PendulumConfig> = {
    g: 9.81,
    L: 1,
    theta0: 0.2,
    omega0: 0,
    dampingCoefficient: 0.02,
    dt: 0.001,
    duration: 5,
    sampleRate: 60,
    mass: 1,
    ...config,
  };

  const { g, L, theta0, omega0, dampingCoefficient, dt, duration, sampleRate, mass } = merged;

  if (L <= 0) throw new RangeError('Length L must be greater than zero');
  if (g <= 0) throw new RangeError('Gravity g must be greater than zero');
  if (dt <= 0 || duration <= 0) throw new RangeError('Time step and duration must be positive');

  const totalSteps = Math.max(1, Math.ceil(duration / dt));
  const sampleStep = Math.max(1, Math.ceil(1 / sampleRate / dt));
  const states: PendulumState[] = [{ theta: theta0, omega: omega0, time: 0 }];

  let current = states[0];
  for (let step = 1; step <= totalSteps; step += 1) {
    current = rk4Step(current, dt, { g, L, dampingCoefficient, mass });
    if (step % sampleStep === 0 || step === totalSteps) {
      states.push({ theta: current.theta, omega: current.omega, time: current.time });
    }
  }

  return {
    states,
    energies: states.map((state) => pendulumEnergy(state.theta, state.omega, L, g, mass)),
    periodEstimate: smallAnglePeriod(L, g),
    averagePeriod: undefined as number | undefined,
  };
}

export function calculateLinearFit(points: Array<{ x: number; y: number }>) {
  if (!points.length) return { slope: 0, intercept: 0, rSquared: 0 };

  const xMean = points.reduce((sum, { x }) => sum + x, 0) / points.length;
  const yMean = points.reduce((sum, { y }) => sum + y, 0) / points.length;

  const numerator = points.reduce((sum, { x, y }) => sum + (x - xMean) * (y - yMean), 0);
  const denominator = points.reduce((sum, { x }) => sum + (x - xMean) ** 2, 0);
  const slope = denominator === 0 ? 0 : numerator / denominator;
  const intercept = yMean - slope * xMean;

  const ssRes = points.reduce((sum, { x, y }) => {
    const predicted = intercept + slope * x;
    return sum + (y - predicted) ** 2;
  }, 0);
  const ssTot = points.reduce((sum, { y }) => sum + (y - yMean) ** 2, 0);

  return {
    slope,
    intercept,
    rSquared: ssTot === 0 ? 1 : 1 - ssRes / ssTot,
  };
}

export function gravityFromSlope(slope: number): number {
  return 4 * Math.PI ** 2 / slope;
}
